import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSetReconcileDate } from '../hooks/useSetReconcileDate';

interface ReconcileDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: number | null;
  userName: string;
}

/**
 * Modal for setting the reconcile date on an estimator's commission account
 * This "freezes" the account - commissions before this date are considered settled
 * and excluded from balance calculations.
 * Admin-only functionality.
 */
export function ReconcileDateModal({
  isOpen,
  onClose,
  onSuccess,
  userId,
  userName,
}: ReconcileDateModalProps) {
  const [reconcileDate, setReconcileDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [error, setError] = useState('');

  const { setReconcileDate: setReconcileDateApi, isLoading } = useSetReconcileDate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!userId) {
      setError('No estimator selected');
      return;
    }

    if (!reconcileDate) {
      setError('Please select a date');
      return;
    }

    const success = await setReconcileDateApi(userId, reconcileDate);

    if (success) {
      resetForm();
      onSuccess();
    } else {
      setError('Failed to set reconcile date. Please try again.');
    }
  };

  const resetForm = () => {
    setReconcileDate(new Date().toISOString().split('T')[0]);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set Reconcile Date</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            Setting a reconcile date for <strong>{userName}</strong> will "freeze"
            their account. Commissions before this date will be considered settled
            and excluded from the account balance calculation.
          </p>

          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="reconcile-date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reconcile Date *
            </label>
            <Input
              id="reconcile-date"
              type="date"
              value={reconcileDate}
              onChange={(e) => setReconcileDate(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              All transactions on or before this date will be excluded from the balance.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-secondary text-white hover:bg-secondary/90"
            >
              {isLoading ? 'Saving...' : 'Set Reconcile Date'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
